import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { FadeUp, ChapterLabel } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ rating }) => (
  <span className="flex gap-1" aria-label={`${rating} sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={15}
        className={i <= Math.round(rating) ? "fill-clay text-clay" : "text-ink/20"}
      />
    ))}
  </span>
);

const Testimonials = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((r) => setData(r.data))
      .catch(() => setData({ configured: false, reviews: [] }));
  }, []);

  if (!data || !data.configured || !data.reviews?.length) return null;

  return (
    <section className="bg-plaster/60 py-24 lg:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <ChapterLabel number="04" label="Avis clients" />
            <FadeUp delay={0.1}>
              <h2 className="mt-8 font-serif text-4xl tracking-tight sm:text-5xl">
                Ils nous ont fait <em className="italic text-clay">confiance.</em>
              </h2>
            </FadeUp>
          </div>
          {data.rating && (
            <FadeUp delay={0.2}>
              <div className="flex items-center gap-4">
                <Stars rating={data.rating} />
                <p className="text-sm text-bark">
                  {data.rating}/5 — {data.review_count} avis Google
                </p>
              </div>
            </FadeUp>
          )}
        </div>
        <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
          {data.reviews.map((r, i) => (
            <FadeUp key={`${r.author}-${i}`} delay={(i % 3) * 0.1} className={i % 3 === 1 ? "lg:translate-y-8" : ""}>
              <figure className="flex h-full flex-col bg-cream p-8" data-testid={`testimonial-${i}`}>
                <Stars rating={r.rating} />
                <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-ink">
                  « {r.text} »
                </blockquote>
                <figcaption className="mt-6">
                  <p className="text-sm font-medium">{r.author}</p>
                  {r.relative_time && <p className="mt-1 text-xs text-taupe">{r.relative_time}</p>}
                  {r.google_maps_uri && (
                    <a
                      href={r.google_maps_uri}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`testimonial-link-${i}`}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-clay underline-offset-2 hover:underline"
                    >
                      Voir cet avis sur Google Maps <ExternalLink size={11} />
                    </a>
                  )}
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-14">
          <p className="text-xs text-taupe">
            Avis Google Maps, affichés par pertinence. Les avis ne sont pas vérifiés par Google.
          </p>
        </FadeUp>
      </div>
    </section>
  );
};

export default Testimonials;
